import { useContext, useMemo, useState, useCallback } from 'react';
import { Box, Grid, Tab, TextareaAutosize, Typography } from '@mui/material';
import { Paper } from '@mui/material';
import { toast } from 'react-toastify';
import { LoadingButton, TabPanel } from '@mui/lab';
import useStyles from '../styles';
import PageHeading from '../../../Components/PageHeading';
import { UserContext } from '../../../Contexts/UserContext';
import { fetchExperimentalBlockSites } from '../../../api/expbDoc';
import { triggerNetworkWideSync } from '../../../api/sites';
import GeneralTab from '../../../Components/GeneralTabAndTabContent';
import { CONFIG_SYNC_MODES } from '../../../core/constants';
import SyncSelectedSitesPanel from './SyncSelectedSitesPanel';
import { SyncAllSitesPanel } from './SyncAllSites';

export default function NetworkWideSync() {
	const classes = useStyles();
	const { user } = useContext(UserContext);
	const [loading, setLoading] = useState({
		experimentalSites: false,
		triggeringSync: false,
		triggeringSRSync: false
	});
	const [experimentalSites, setExperimentalSites] = useState([]);
	const [selectedSites, setSelectedSites] = useState([]);
	const [activeTab, setActiveTab] = useState(CONFIG_SYNC_MODES.SYNC_ALL);

	const getMergedSiteIdsFromResponse = (response) => {
		const { experimentalSites = [], nonActiveSites = [] } = response?.data?.data || {};

		const experimentalSiteIds = Array.isArray(experimentalSites) ? experimentalSites : [];
		const nonActiveSiteIds = Array.isArray(nonActiveSites) ? nonActiveSites : [];

		return Array.from(
			new Set(
				[...experimentalSiteIds, ...nonActiveSiteIds].filter(
					(siteId) => siteId !== null && siteId !== ''
				)
			)
		);
	};

	const fetchAndPopulateExperimentalSites = useCallback(async () => {
		setLoading((prevState) => ({ ...prevState, experimentalSites: true }));

		try {
			const experimentalSitesResponse = await fetchExperimentalBlockSites();

			const mergedSites = getMergedSiteIdsFromResponse(experimentalSitesResponse);

			setExperimentalSites(mergedSites);

			toast.success('Fetched Experimental Sites', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		} catch (error) {
			const errorMessage = error?.response?.data?.data?.message || error.message;
			toast.error(errorMessage, {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		} finally {
			setLoading((prevState) => ({ ...prevState, experimentalSites: false }));
		}
	}, []);

	const networkWideSyncHandler = useCallback(async () => {
		setLoading((prevState) => ({ ...prevState, triggeringSync: true }));
		try {
			if (activeTab === CONFIG_SYNC_MODES.SYNC_ALL) {
				// Sync all sites, excluding those in excludedSites
				await triggerNetworkWideSync({ syncMode: activeTab, excludedSites: experimentalSites });
			} else if (activeTab === CONFIG_SYNC_MODES.SYNC_SELECTED) {
				// Sync only selected sites
				await triggerNetworkWideSync({ syncMode: activeTab, includedSites: selectedSites });
			}
			toast.success('Triggered Sync', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		} catch (error) {
			const errorMessage = error?.response?.data?.data?.message || error.message;
			toast.error(errorMessage, {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		} finally {
			setLoading((prevState) => ({ ...prevState, triggeringSync: false }));
		}
	}, [activeTab, experimentalSites, selectedSites]);

	const tabListAndPanels = useMemo(
		() => [
			{
				tab: (
					<Tab
						label="Sync All Sites"
						value={CONFIG_SYNC_MODES.SYNC_ALL}
						onClick={() => setActiveTab(CONFIG_SYNC_MODES.SYNC_ALL)}
					/>
				),
				tabPanel: (
					<TabPanel value={CONFIG_SYNC_MODES.SYNC_ALL} style={{ width: '100%' }}>
						<SyncAllSitesPanel
							experimentalSites={experimentalSites}
							setExperimentalSites={setExperimentalSites}
							loading={loading}
							fetchAndPopulateExperimentalSites={fetchAndPopulateExperimentalSites}
							networkWideSyncHandler={networkWideSyncHandler}
						/>
					</TabPanel>
				)
			},
			{
				tab: (
					<Tab
						label="Sync Selected Sites"
						value={CONFIG_SYNC_MODES.SYNC_SELECTED}
						onClick={() => setActiveTab(CONFIG_SYNC_MODES.SYNC_SELECTED)}
					/>
				),
				tabPanel: (
					<TabPanel value={CONFIG_SYNC_MODES.SYNC_SELECTED} style={{ width: '100%' }}>
						<SyncSelectedSitesPanel
							selectedSites={selectedSites}
							setSelectedSites={setSelectedSites}
							loading={loading}
							setLoading={setLoading}
							networkWideSyncHandler={networkWideSyncHandler}
						/>
					</TabPanel>
				)
			}
		],
		[
			experimentalSites,
			selectedSites,
			loading,
			fetchAndPopulateExperimentalSites,
			networkWideSyncHandler
		]
	);

	return (
		<Paper className={classes.dashboardMainContent}>
			<PageHeading
				headingName="Network Wide Sync"
				headingPath={['Dashboard', 'Network Wide Sync']}
				description="Sync network wide sites with the latest configuration"
			/>
			{!user.isSuperAdmin && (
				<Typography variant="h6" className={classes.heading} fontWeight="bold" marginTop="10rem">
					Contact Administrator for Network Wide Sync Access
				</Typography>
			)}
			{user.isSuperAdmin && (
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
					<GeneralTab tabListAndPanels={tabListAndPanels} tabListStyles={{ marginTop: '2rem' }} />
				</Box>
			)}
		</Paper>
	);
}
